import React, { useState, useRef, useEffect, useMemo } from 'react';
import { TextField } from '@adobe/react-spectrum';
import Edit from '@spectrum-icons/workflow/Edit';
import { useDispatch } from 'react-redux';
import { useHomeSlice } from 'app/pages/Home/slice';
import { isEolMoment } from 'utils/constants';
import {
  getEolPhaseTitlePrefix,
  stripEolPhaseNamePrefix
} from 'utils/eolPhaseTitlePrefix';
import { CardTitleDisplay } from './CardTitleDisplay';
import eolFormStyles from '../RsdForms/CreateMoment/FormField.module.scss';
import styles from './TimelineCard.module.scss';

interface EditTitleProps {
  /** Full line shown in the card (may include "VIP: …" for display). */
  title: string;
  /** When set with `phaseTitleLabel`, edits/API use this value only (no phase prefix). */
  storageTitle?: string;
  /** RTM phase name for display-only prefix (VIP, ETLA, …). */
  phaseTitleLabel?: string;
  /** EOL release phase (Initial Announcement, End of Sale, …) for stage prefix. */
  releasePhase?: string;
  onSubmit?: (newTitle: string) => void;
  className?: string;
  placeholder?: string;
  isDateClicked: boolean;
  issueId: string;
  momentType: string;
}

function resolveEditableTitle(
  title: string,
  storageTitle: string | undefined,
  usePhaseSplit: boolean,
  isEol: boolean
): string {
  const raw = usePhaseSplit ? storageTitle ?? title : title;
  return isEol ? stripEolPhaseNamePrefix(raw) : raw;
}

export const EditTitle: React.FC<EditTitleProps> = ({
  title,
  storageTitle,
  phaseTitleLabel,
  releasePhase,
  onSubmit,
  className = '',
  isDateClicked,
  momentType,
  placeholder = 'Enter Title'
}) => {
  const usePhaseSplit =
    Boolean(phaseTitleLabel?.trim()) && storageTitle !== undefined;
  const isEol = isEolMoment(momentType);
  const eolPrefixLabel = getEolPhaseTitlePrefix(releasePhase);

  const baselineTitle = useMemo(
    () => resolveEditableTitle(title, storageTitle, usePhaseSplit, isEol),
    [title, storageTitle, usePhaseSplit, isEol]
  );

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(baselineTitle);
  const dispatch = useDispatch();
  const { actions } = useHomeSlice();
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const isEmpty = editValue.trim() === '';

  useEffect(() => {
    setEditValue(baselineTitle);
  }, [baselineTitle, phaseTitleLabel, isDateClicked]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleTitleClick = () => {
    if (isDateClicked) setIsEditing(true);
  };

  const handleTitleChange = (value: string) => {
    setEditValue(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (value.trim()) {
      debounceTimerRef.current = setTimeout(() => {
        dispatch(
          actions.updateTooltipDetails({
            title: value
          })
        );
      }, 150);
    }
  };

  const handleTitleSave = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (editValue !== baselineTitle && !isEmpty) {
      dispatch(
        actions.updateTooltipDetails({
          title: editValue
        })
      );
      if (onSubmit) {
        onSubmit(editValue);
      }
    }
    setIsEditing(false);
  };

  const handleTitleCancel = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setEditValue(baselineTitle);
    setIsEditing(false);
  };

  const handleTitleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleTitleSave();
    } else if (event.key === 'Escape') {
      handleTitleCancel();
    }
  };

  const renderDisplayTitle = () => {
    if (usePhaseSplit) {
      return (
        <>
          {phaseTitleLabel}:{' '}
          {isEol ? (
            <CardTitleDisplay
              projectType={momentType}
              releasePhase={releasePhase}
              title={editValue}
              displayTitle={editValue}
            />
          ) : (
            editValue
          )}
        </>
      );
    }
    if (isEol) {
      return (
        <CardTitleDisplay
          projectType={momentType}
          releasePhase={releasePhase}
          title={editValue}
          displayTitle={editValue}
        />
      );
    }
    return editValue;
  };

  const eolTitleField = (
    <TextField
      value={editValue}
      onChange={handleTitleChange}
      onBlur={handleTitleSave}
      onKeyDown={handleTitleKeyDown}
      UNSAFE_className={`${eolFormStyles.textField} ${eolFormStyles.eolNameInput}`}
      autoFocus
      aria-label="Edit title"
      validationState={isEmpty ? 'invalid' : undefined}
      placeholder={placeholder}
    />
  );

  if (isDateClicked && isEditing) {
    return (
      <div className={styles.editingBox}>
        {isEol ? (
          <div
            className={[
              eolFormStyles.formField,
              eolFormStyles.eolNameWithPrepend,
              isEmpty ? eolFormStyles.error : ''
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div className={eolFormStyles.eolNameFieldGroup}>
              <span
                className={eolFormStyles.eolNamePrependText}
                aria-hidden="true"
              >
                {eolPrefixLabel}
              </span>
              {eolTitleField}
            </div>
          </div>
        ) : (
          <TextField
            value={editValue}
            onChange={handleTitleChange}
            onBlur={handleTitleSave}
            onKeyDown={handleTitleKeyDown}
            UNSAFE_className={`${styles.titleInput} ${
              isEmpty ? styles.errorBorder : ''
            }`}
            autoFocus
            aria-label="Edit title"
            validationState={isEmpty ? 'invalid' : undefined}
            placeholder={placeholder}
          />
        )}
        {isEmpty && (
          <div className={styles.errorMessage}>Title can't be empty</div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.titleWrapper}>
      <div
        className={`${styles.clickableTitle} ${
          !isDateClicked ? styles.readOnlyTitle : ''
        }`}
        onClick={handleTitleClick}
      >
        <div className={`${styles.tooltipValue} ${className}`}>
          {renderDisplayTitle()}
        </div>
      </div>
      {isDateClicked && (
        <button
          className={styles.editButton}
          onClick={handleTitleClick}
          aria-label="Edit title"
          title="Edit title"
        >
          <Edit size="S" />
        </button>
      )}
    </div>
  );
};
